import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { getProjects } from "@/features/leaderboard/api/leaderboard-api.ts";
import type { Project } from "@/features/leaderboard/types/index.ts";
import { useCoInvestorComparables } from "../hooks/use-intelligence-queries.ts";
import { ProjectNameLink } from "./ProjectNameLink.tsx";

function formatDollars(n: number | null): string {
  if (n == null) return "—";
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}

interface CoInvestorFinderProps {
  onOpenProjectDetail?: (project: Project) => void;
}

export function CoInvestorFinder({ onOpenProjectDetail }: CoInvestorFinderProps = {}) {
  const [query, setQuery] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [selectedName, setSelectedName] = useState<string | null>(null);

  // Suggest projects as the user types. Skip while typing <2 chars.
  // Key on the trimmed value so leading/trailing whitespace variants
  // ("Solana", " Solana", "Solana ") hit the same cache entry rather
  // than thrashing with three separate queries.
  const trimmedQuery = query.trim();
  const { data: suggestions } = useQuery({
    queryKey: ["intel-coinv-search", trimmedQuery],
    queryFn: () => getProjects({ filters: { search: trimmedQuery }, limit: 8 }),
    enabled: trimmedQuery.length >= 2 && selectedId == null,
    staleTime: 30_000,
  });

  const { data: result, isLoading, error } = useCoInvestorComparables(selectedId, 3);

  const pickProject = (id: string, name: string) => {
    setSelectedId(id);
    setSelectedName(name);
    setQuery(name);
  };

  const clearSelection = () => {
    setSelectedId(null);
    setSelectedName(null);
    setQuery("");
  };

  return (
    <section className="intel-section">
      <header className="intel-section__header">
        <h3 className="intel-section__title">Co-investor / deal comparables</h3>
        <p className="intel-section__subtitle">
          Type a project name → the finder returns other projects sharing 3+ investors with it, the
          median raise across the comparable set, and the median score. Direct negotiation anchor
          for BD: "your comparable set is N deals at median $X."
        </p>
      </header>

      <div className="intel-coinv-picker">
        <input
          type="text"
          placeholder="Type a project name…"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            if (selectedId) {
              setSelectedId(null);
              setSelectedName(null);
            }
          }}
          className="intel-coinv-search"
        />
        {selectedId && (
          <button type="button" className="intel-coinv-clear" onClick={clearSelection}>
            Clear
          </button>
        )}
      </div>

      {!selectedId && suggestions && suggestions.data.length > 0 && (
        <ul className="intel-coinv-suggestions">
          {suggestions.data.slice(0, 8).map((p) => (
            <li key={p.id}>
              <button
                type="button"
                className="intel-coinv-suggestion"
                onClick={() => pickProject(p.id, p.name ?? p.twitter_handle)}
              >
                <span className="intel-coinv-suggestion__name">{p.name ?? p.twitter_handle}</span>
                <span className="intel-coinv-suggestion__score">
                  score{" "}
                  {p.current_alpha_score != null && Number.isFinite(Number(p.current_alpha_score))
                    ? Number(p.current_alpha_score).toFixed(1)
                    : "—"}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      {isLoading && selectedId && (
        <div className="intel-loading">Loading comparables for {selectedName}…</div>
      )}

      {error && selectedId && !isLoading && (
        <div className="intel-coinv-empty">
          Couldn't load comparables for {selectedName}. The project may be missing investors or an
          alpha score.
        </div>
      )}

      {result && (
        <div className="intel-coinv-result">
          <div className="intel-coinv-summary">
            <div className="intel-coinv-summary__card">
              <div className="intel-coinv-summary__label">Target</div>
              <div className="intel-coinv-summary__value">
                <ProjectNameLink
                  projectId={result.target.projectId}
                  label={result.target.name ?? result.target.twitterHandle}
                  onOpenProjectDetail={onOpenProjectDetail}
                />
              </div>
              <div className="intel-coinv-summary__meta">
                score {result.target.alphaScore.toFixed(1)} · raised{" "}
                {formatDollars(result.target.fundingRaisedUsd)} · {result.target.totalInvestors}{" "}
                investors
              </div>
            </div>
            <div className="intel-coinv-summary__card">
              <div className="intel-coinv-summary__label">Comparable set</div>
              <div className="intel-coinv-summary__value">
                {result.totalQualifying} projects
                {result.totalQualifying > result.comparables.length && (
                  <span className="intel-coinv-summary__truncated">
                    {" "}
                    (showing top {result.comparables.length})
                  </span>
                )}
              </div>
              <div className="intel-coinv-summary__meta">
                median raise {formatDollars(result.medianComparableRaiseUsd)} · median score{" "}
                {result.medianComparableScore != null
                  ? result.medianComparableScore.toFixed(1)
                  : "—"}
              </div>
            </div>
          </div>

          {result.comparables.length === 0 ? (
            <div className="intel-coinv-empty">
              No other projects share 3+ investors with{" "}
              {result.target.name ?? result.target.twitterHandle}. Try a project with a larger cap
              table.
            </div>
          ) : (
            <div className="intel-coinv-wrap">
              <table className="intel-coinv-table">
                <thead>
                  <tr>
                    <th>Project</th>
                    <th>Family</th>
                    <th className="intel-coinv-table__num-head">Score</th>
                    <th className="intel-coinv-table__num-head">Raised</th>
                    <th className="intel-coinv-table__num-head">Shared</th>
                    <th>Shared investors (top 5)</th>
                  </tr>
                </thead>
                <tbody>
                  {result.comparables.map((c) => (
                    <tr key={c.projectId}>
                      <td className="intel-coinv-table__project">
                        <ProjectNameLink
                          projectId={c.projectId}
                          label={c.name ?? c.twitterHandle}
                          onOpenProjectDetail={onOpenProjectDetail}
                        />
                      </td>
                      <td>{c.family ?? "Other"}</td>
                      <td className="intel-coinv-table__num">{c.alphaScore.toFixed(1)}</td>
                      <td className="intel-coinv-table__num">
                        {formatDollars(c.fundingRaisedUsd)}
                      </td>
                      <td className="intel-coinv-table__num intel-coinv-table__shared-count">
                        {c.sharedInvestors.length}
                      </td>
                      <td className="intel-coinv-table__shared">
                        {c.sharedInvestors.slice(0, 5).join(", ")}
                        {c.sharedInvestors.length > 5 && ` +${c.sharedInvestors.length - 5} more`}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </section>
  );
}
